// src/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async (token) => {
    try {
      const res = await fetch('/api/user-permissions/', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) return await res.json();
    } catch (e) {
      console.error('Failed to load permissions', e);
    }
    return null;
  }, []);

  const verifyAndLoad = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      const verifyRes = await fetch('/api/verify-token/', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!verifyRes.ok) {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      // Сохраняем токен в Android (если нужно)
      if (window.Android?.saveAuthToken) {
        window.Android.saveAuthToken(token);
      }

      const permissions = await loadPermissions(token);
      setUserPermissions(permissions);
      setIsAuthenticated(true);
    } catch (e) {
      console.error('Auth check failed', e);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [loadPermissions]);

  useEffect(() => {
    verifyAndLoad();
  }, [verifyAndLoad]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userPermissions,
        loading,
        refetch: verifyAndLoad,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};